import React, { createContext, ReactNode, useContext, useReducer } from 'react'

import { AllowedInsuranceTypes } from '@customTypes/Insurance'
import { AllowedPatientTypes } from '@customTypes/Patient'
import { SelectedSlot } from '@customTypes/Availabilities'
import { Choices, InitialState } from '@customTypes/Choices'

import { BookingFor } from '@customTypes/BookingFor'
import { addDebuggingData } from 'src/utils/addDebuggingData'
import { cloneDeep, uniq } from 'lodash'
import { InsuranceTypeOptions } from 'src/config/insuranceTypeOptions'

export enum actionTypes {
  setTreatment = 'setTreatment',
  setPatientType = 'setPatientType',
  setPatientReferenceIdAndPreferredPractitionerId = 'setPatientReferenceIdAndPreferredPractitionerId',
  setInsuranceType = 'setInsuranceType',
  setBookingFor = 'setBookingFor',
  setPreferredPractitioners = 'setPreferredPractitioners',
  setPreferredPractitionersAfterFetching = 'setPreferredPractitionersAfterFetching',
  setSelectedBookable = 'setSelectedBookable',
  setArrivalTime = 'setArrivalTime',
  setAppointmentDuration = 'setAppointmentDuration',
  resetSelectedBookable = 'resetSelectedBookable',
  setIsManualReviewRequired = 'setIsManualReviewRequired',
  setInitialActionPerformed = 'setInitialActionPerformed',
  setInitialState = 'setInitialState',
  resetInitialState = 'resetInitialState',
  resetDefaultChoice = 'resetDefaultChoice',
  setAllPreferredPractitionersAreSelected = 'setAllPreferredPractitionersAreSelected',
  setValidatedPhoneNumber = 'setValidatedPhoneNumber',
  setDistrict = 'setDistrict',
}

type Action = {
  type: actionTypes
  payload: any
}

type Dispatch = (action: Action) => void
type ChoicesProviderProps = { children: ReactNode; initialState?: Partial<Choices> }

const ChoiceStateContext = createContext<Choices | undefined>(undefined)
const ChoiceDispatchContext = createContext<Dispatch | undefined>(undefined)

export const defaultChoice: Choices = {
  isManualReviewRequired: false,
  treatmentBundleTypeReferenceId: '',
  preferredPractitionerReferenceIds: [],
  allPreferredPractitionersAreSelected: false,
  patientType: undefined,
  verifiedPatientData: {
    patientReferenceId: undefined,
    preferredPractitionerReferenceId: undefined,
  },
  insuranceType: InsuranceTypeOptions.SOCIAL_INSURANCE,
  selectedBookableDateAndTime: {
    day: '',
    time: '',
    originalStartTimestamp: '',
    clinicId: '',
  },
  arrivalTime: undefined,
  appointmentDuration: undefined,
  bookingFor: undefined,
  initialState: {
    treatmentBundleTypeReferenceId: undefined,
    initialActionPerformed: false,
  },
  validatedPhoneNumber: '',
  district: undefined,
}

export const choiceReducer = (state: Choices, action: Action): Choices => {
  switch (action.type) {
    case actionTypes.setTreatment: {
      const { id } = action.payload
      const treatmentChanged = state.treatmentBundleTypeReferenceId !== id

      if (treatmentChanged) {
        return {
          ...state,
          treatmentBundleTypeReferenceId: id,
          preferredPractitionerReferenceIds: [],
        }
      }

      return state
    }
    case actionTypes.setPatientType: {
      const patientTypeChanged = state.patientType !== action.payload
      if (patientTypeChanged) {
        return { ...state, patientType: action.payload, preferredPractitionerReferenceIds: [] }
      }

      return { ...state, patientType: action.payload }
    }
    case actionTypes.setPatientReferenceIdAndPreferredPractitionerId:
      const verifiedPatientData = {
        patientReferenceId: action.payload.patientReferenceId,
        preferredPractitionerReferenceId: action.payload.preferredPractitionerReferenceId,
      }
      return {
        ...state,
        verifiedPatientData,
      }
    case actionTypes.setInsuranceType:
      return { ...state, insuranceType: action.payload }
    case actionTypes.setBookingFor:
      return { ...state, bookingFor: action.payload }
    case actionTypes.setPreferredPractitioners:
      return { ...state, preferredPractitionerReferenceIds: action.payload }
    case actionTypes.setPreferredPractitionersAfterFetching: {
      // If all practitioners were selected before fetching -> add new ones to preferredPractitionerReferenceIds
      // If only some preferred practitioners were selected -> don't add to preferredPractitionerReferenceIds
      const { practitionerIds }: SetPreferredPractitionersAfterFetchingPayload = action.payload
      if (state.allPreferredPractitionersAreSelected) {
        const newPractitionerIds = practitionerIds
        const currentPractitionerIds = state.preferredPractitionerReferenceIds
        const preferredPractitionerReferenceIds = uniq([...currentPractitionerIds, ...newPractitionerIds])

        return { ...state, preferredPractitionerReferenceIds }
      } else {
        return { ...state }
      }
    }
    case actionTypes.setAllPreferredPractitionersAreSelected:
      return { ...state, allPreferredPractitionersAreSelected: action.payload }
    case actionTypes.setSelectedBookable:
      return { ...state, selectedBookableDateAndTime: action.payload }
    case actionTypes.resetSelectedBookable:
      return { ...state, selectedBookableDateAndTime: defaultChoice.selectedBookableDateAndTime }
    case actionTypes.setArrivalTime:
      return { ...state, arrivalTime: action.payload }
    case actionTypes.setAppointmentDuration:
      return { ...state, appointmentDuration: action.payload }
    case actionTypes.setIsManualReviewRequired:
      return { ...state, isManualReviewRequired: action.payload }
    case actionTypes.setInitialState:
      return { ...state, initialState: { ...state.initialState, ...action.payload } }
    case actionTypes.setInitialActionPerformed:
      return { ...state, initialState: { ...state.initialState, initialActionPerformed: true } }
    case actionTypes.setValidatedPhoneNumber:
      return { ...state, validatedPhoneNumber: action.payload }
    case actionTypes.resetInitialState:
      return { ...state, initialState: cloneDeep(defaultChoice.initialState) }
    case actionTypes.setDistrict:
      return { ...state, district: action.payload }
    case actionTypes.resetDefaultChoice:
      return cloneDeep(defaultChoice)
    default: {
      return state
    }
  }
}

export const ChoicesProvider = ({ children, initialState }: ChoicesProviderProps) => {
  const [state, dispatch] = useReducer(choiceReducer, { ...defaultChoice, ...initialState })
  addDebuggingData('choiceState', state)

  return (
    <ChoiceStateContext.Provider value={state}>
      <ChoiceDispatchContext.Provider value={dispatch}>{children}</ChoiceDispatchContext.Provider>
    </ChoiceStateContext.Provider>
  )
}

export const useChoiceState = () => {
  const context = useContext(ChoiceStateContext)
  if (context === undefined) {
    throw new Error('useChoiceState must be user within a ChoicesProvider')
  }
  return context
}

export const useChoiceDispatch = () => {
  const context = useContext(ChoiceDispatchContext)
  if (context === undefined) {
    throw new Error('useChoiceDispatch must be used within a ChoicesProvider')
  }
  return context
}

export const setPatientType = (payload: AllowedPatientTypes): Action => ({
  type: actionTypes.setPatientType,
  payload,
})

export const setPatientReferenceIdAndPreferredPractitionerId = (payload: {
  patientReferenceId: string
  preferredPractitionerReferenceId?: string
}): Action => ({
  type: actionTypes.setPatientReferenceIdAndPreferredPractitionerId,
  payload,
})

export const setTreatment = (id: string): Action => ({
  type: actionTypes.setTreatment,
  payload: {
    id,
  },
})
export const setIsManualReviewRequired = (manualReviewRequired: boolean): Action => ({
  type: actionTypes.setIsManualReviewRequired,
  payload: manualReviewRequired,
})

export const setInsuranceType = (payload: AllowedInsuranceTypes): Action => ({
  type: actionTypes.setInsuranceType,
  payload,
})

export const setBookingFor = (payload: BookingFor): Action => ({
  type: actionTypes.setBookingFor,
  payload,
})

export const setPreferredPractitioners = (payload: string[]): Action => ({
  type: actionTypes.setPreferredPractitioners,
  payload,
})

type SetPreferredPractitionersAfterFetchingPayload = {
  practitionerIds: string[]
}
export const setPreferredPractitionersAfterFetching = (
  payload: SetPreferredPractitionersAfterFetchingPayload
): Action => ({
  type: actionTypes.setPreferredPractitionersAfterFetching,
  payload,
})

export const setSelectedBookable = (payload: SelectedSlot): Action => ({
  type: actionTypes.setSelectedBookable,
  payload,
})

export const resetSelectedBookable = (): Action => ({
  type: actionTypes.resetSelectedBookable,
  payload: '',
})

export const setArrivalTime = (payload: string): Action => ({ type: actionTypes.setArrivalTime, payload })

export const setAppointmentDuration = (payload: number): Action => ({
  type: actionTypes.setAppointmentDuration,
  payload,
})

export const setInitialActionPerformed = (): Action => ({
  type: actionTypes.setInitialActionPerformed,
  payload: '',
})

export const setInitialState = (payload: Partial<InitialState>): Action => ({
  type: actionTypes.setInitialState,
  payload,
})

export const resetInitialState = (): Action => ({
  type: actionTypes.resetInitialState,
  payload: '',
})

export const setValidatedPhoneNumber = (payload: string): Action => ({
  type: actionTypes.setValidatedPhoneNumber,
  payload,
})

export const resetDefaultChoice = (): Action => ({
  type: actionTypes.resetDefaultChoice,
  payload: '',
})

export const setAllPractitionersAreSelected = (payload: boolean): Action => ({
  type: actionTypes.setAllPreferredPractitionersAreSelected,
  payload,
})

export const setDistrict = (payload: string): Action => ({
  type: actionTypes.setDistrict,
  payload,
})
